import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Heart, 
    Users, 
    User, 
    ChevronRight, 
    RefreshCw,
    MessageCircle,
    Shield,
    Compass,
    Wind,
    CheckCircle2,
    Circle,
    ArrowLeft
} from 'lucide-react';
import { PrimaryButton, SecondaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';
import { trackGlobalToolUsage } from './UnifiedHome';
import { getCheckinQuestions } from '../services/relationshipToolsService';
import { AdminEditButton } from '../components/admin/AdminEditButton';

/**
 * RelationshipCheckIn - Relationship Health Assessment
 * 
 * A guided check-in that helps users identify relationship patterns
 * and recommends the right tools based on their answers.
 * Now fetches questions from the database.
 * 
 * Flow: Welcome → Mode (Solo/Partner) → Questions → Results with Recommendations
 */

// Icon mapping for questions from database
const ICON_MAP = {
    'RefreshCw': RefreshCw,
    'MessageCircle': MessageCircle,
    'Heart': Heart,
    'Shield': Shield,
    'Users': Users,
    'Compass': Compass,
    'Wind': Wind
};

// Transform database question to component format
const transformQuestion = (dbQuestion) => ({
    id: dbQuestion.question_id,
    question: dbQuestion.question,
    subtext: dbQuestion.subtext,
    icon: ICON_MAP[dbQuestion.icon] || Heart,
    options: dbQuestion.options.map(opt => ({
        value: opt.value,
        label: opt.label,
        color: opt.color
    }))
});

// Fallback questions if database is unavailable
const FALLBACK_QUESTIONS = [
    {
        id: 'same_argument',
        question: 'Do you seem to be having the same argument over and over again?',
        subtext: 'The topic might change, but the feeling is familiar.',
        icon: RefreshCw,
        options: [
            { value: 'often', label: 'Yes, often', color: 'bg-[#FFBB98]' },
            { value: 'sometimes', label: 'Sometimes', color: 'bg-[#FFBB98]' },
            { value: 'rarely', label: 'Rarely or never', color: 'bg-[#FFBB98]' }
        ]
    },
    {
        id: 'nothing_resolved',
        question: 'Do you feel like nothing ever gets resolved?',
        subtext: 'Conversations end, but the tension remains.',
        icon: MessageCircle,
        options: [
            { value: 'often', label: 'Yes, often', color: 'bg-[#FFBB98]' },
            { value: 'sometimes', label: 'Sometimes', color: 'bg-[#FFBB98]' },
            { value: 'rarely', label: 'Rarely or never', color: 'bg-[#FFBB98]' }
        ]
    },
    {
        id: 'unmet_needs',
        question: 'Do you have needs that feel unmet in your relationship?',
        subtext: 'Needs for connection, support, understanding, or space.',
        icon: Heart,
        options: [
            { value: 'often', label: 'Yes, often', color: 'bg-[#FFBB98]' },
            { value: 'sometimes', label: 'Sometimes', color: 'bg-[#FFBB98]' },
            { value: 'rarely', label: 'Rarely or never', color: 'bg-[#FFBB98]' }
        ]
    }
];

const RelationshipCheckIn = () => {
    const navigate = useNavigate();
    
    // Flow state
    const [step, setStep] = useState('welcome'); // welcome, mode, questions, results
    const [mode, setMode] = useState(null); // 'solo' or 'partner'
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState({});
    const [fadeIn, setFadeIn] = useState(false);
    
    // Data from database
    const [questions, setQuestions] = useState(FALLBACK_QUESTIONS);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setTimeout(() => setFadeIn(true), 100);
    }, []);

    useEffect(() => {
        setFadeIn(false);
        const timer = setTimeout(() => setFadeIn(true), 50);
        return () => clearTimeout(timer);
    }, [step, currentQuestion]);

    // Fetch questions from database
    useEffect(() => {
        const fetchData = async () => {
            try {
                const questionsData = await getCheckinQuestions();
                if (questionsData && questionsData.length > 0) {
                    setQuestions(questionsData.map(transformQuestion));
                }
            } catch (error) {
                console.error('Error fetching check-in questions:', error);
                // Keep fallback questions
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Calculate results based on answers
    const getResults = () => {
        const patterns = [];
        const recommendations = [];
        
        // Check for repeating cycle pattern
        if (answers.same_argument === 'often' || answers.nothing_resolved === 'often') {
            patterns.push({
                id: 'cycle',
                title: 'Repeating Cycles',
                description: "You might be caught in a negative cycle - a pattern that repeats even when you don't want it to.",
                severity: 'high',
                icon: RefreshCw
            });
            recommendations.push({
                id: 'break-cycle',
                title: 'Break the Cycle',
                description: 'Explore the pattern and find new ways forward.',
                path: '/relationship-hub/break-the-cycle',
                icon: RefreshCw,
                variant: 'lavender',
                priority: 1
            });
        }

        // Check for unmet needs
        if (answers.unmet_needs === 'often' || answers.unmet_needs === 'sometimes') {
            patterns.push({
                id: 'needs',
                title: 'Unmet Needs',
                description: 'Some of your core needs may not be getting met, which can create distance or resentment over time.',
                severity: answers.unmet_needs === 'often' ? 'high' : 'medium',
                icon: Heart
            });
            recommendations.push({
                id: 'solo-need',
                title: 'What Do I Need?',
                description: 'Get clear on your own needs first.',
                path: '/relationship-hub/solo-need',
                icon: Heart,
                variant: 'rose',
                priority: 2
            });
            recommendations.push({
                id: 'attachment',
                title: 'Attachment Needs',
                description: 'Explore your attachment style and needs.',
                path: '/relationship-hub/attachment-needs',
                icon: Heart,
                variant: 'rose',
                priority: 3
            });
        }

        // Check for shutdown patterns
        if (answers.shutdown === 'often' || answers.partner_shutdown === 'often') {
            patterns.push({
                id: 'shutdown',
                title: 'Shutdown Pattern',
                description: answers.shutdown === 'often' 
                    ? 'You may be withdrawing during conflict as a way to protect yourself. This is common but can leave things unresolved.'
                    : 'Your partner may be withdrawing during conflict. This can feel like rejection but is often self-protection.',
                severity: 'high',
                icon: Shield
            });
            
            if (answers.shutdown === 'often') {
                recommendations.push({
                    id: 'overwhelm',
                    title: 'Regulation Navigator',
                    description: 'Learn to regulate before you shut down.',
                    path: '/regulate/regulation-navigator',
                    icon: Compass,
                    variant: 'sage',
                    priority: 1
                });
            }
            recommendations.push({
                id: 'micro-reset',
                title: 'Micro Reset',
                description: 'Quick tools to stay present during tension.',
                path: '/regulate/micro-reset',
                icon: Wind,
                variant: 'sage',
                priority: 2
            });
        }

        // Check emotional safety
        if (answers.emotional_safety === 'rarely') {
            patterns.push({
                id: 'safety',
                title: 'Emotional Safety Concerns',
                description: "It's hard to connect when you don't feel safe to be yourself. This is worth exploring.",
                severity: 'high',
                icon: Shield
            });
            recommendations.push({
                id: 'safety-check',
                title: 'Relationship Safety',
                description: 'Assess the emotional safety of your relationship.',
                path: '/regulate/relationship-check',
                icon: Shield,
                variant: 'blue',
                priority: 1
            });
        }

        // If partner mode and things are relatively healthy, suggest connection tools
        if (mode === 'partner' && patterns.filter(p => p.severity === 'high').length === 0) {
            recommendations.push({
                id: 'coregulate',
                title: 'Co-Regulation',
                description: 'Connect and calm down together.',
                path: '/regulate/coregulation',
                icon: Users,
                variant: 'blue',
                priority: 3
            });
        }

        // If no major patterns detected
        if (patterns.length === 0) {
            patterns.push({
                id: 'healthy',
                title: 'Looking Good',
                description: "Based on your answers, things seem relatively balanced. That doesn't mean perfect - just that there aren't major warning signs right now.",
                severity: 'low',
                icon: Heart
            });
            recommendations.push({
                id: 'relationship-hub',
                title: 'Relationship Hub',
                description: 'Explore tools to maintain your connection.',
                path: '/relationship-hub',
                icon: Heart,
                variant: 'rose',
                priority: 1
            });
        }

        // Sort recommendations by priority
        recommendations.sort((a, b) => a.priority - b.priority);

        // Dedupe recommendations by id
        const seen = new Set();
        const uniqueRecs = recommendations.filter(rec => {
            if (seen.has(rec.id)) return false;
            seen.add(rec.id);
            return true;
        });

        return { patterns, recommendations: uniqueRecs.slice(0, 3) };
    };

    const handleModeSelect = (selectedMode) => {
        setMode(selectedMode);
        setTimeout(() => setStep('questions'), 300);
    };

    const handleAnswer = (value) => {
        const question = questions[currentQuestion];
        setAnswers(prev => ({ ...prev, [question.id]: value }));
        
        if (currentQuestion < questions.length - 1) {
            setTimeout(() => setCurrentQuestion(prev => prev + 1), 300);
        } else {
            setTimeout(() => setStep('results'), 300);
        }
    };

    const handleToolClick = (tool) => {
        trackGlobalToolUsage(tool.path);
        navigate(tool.path);
    };

    const handleBack = () => {
        if (step === 'questions' && currentQuestion > 0) {
            setCurrentQuestion(prev => prev - 1);
        } else if (step === 'questions') {
            setStep('mode');
        } else if (step === 'mode') {
            setStep('welcome');
        } else if (step === 'results') {
            setStep('questions');
            setCurrentQuestion(questions.length - 1);
        }
    };

    const handleStartOver = () => {
        setStep('welcome');
        setMode(null);
        setCurrentQuestion(0);
        setAnswers({});
    };

    const results = step === 'results' ? getResults() : null;
    const progress = step === 'questions' ? ((currentQuestion + 1) / questions.length) * 100 : 0;

    const variantColors = {
        sage: 'bg-[#FFBB98]/40',
        rose: 'bg-[#FFBB98]/40',
        lavender: 'bg-[#7D8E95]/40',
        blue: 'bg-[#7D8E95]/40'
    };

    const variantIconColors = {
        sage: 'text-[#1D6B6B]',
        rose: 'text-[#FFBB98]',
        lavender: 'text-[#7D8E95]',
        blue: 'text-[#7D8E95]'
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Admin Edit Button */}
            <AdminEditButton toolType="checkin_question" />
            
            {/* Header for non-welcome steps */}
            {step !== 'welcome' && (
                <header className="sticky top-0 z-40 glass-header">
                    <div className="max-w-xl mx-auto px-6 py-4 flex items-center gap-4">
                        <button 
                            onClick={handleBack}
                            className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/50 transition-colors"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5 text-[#7D8E95]" />
                        </button>
                        <div className="flex-1">
                            <SmallText className="text-[#7D8E95] font-medium">Relationship Check-in</SmallText>
                            {step === 'questions' && (
                                <div className="mt-2 h-1.5 bg-[#FFBB98] rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-[#FFBB98] rounded-full transition-all duration-500"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </header>
            )}

            <main className="max-w-md mx-auto px-6 py-12 min-h-screen flex flex-col">
                
                {/* WELCOME */}
                {step === 'welcome' && (
                    <div className={`flex-1 flex flex-col justify-center transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="flex justify-center mb-8">
                            <div className="w-20 h-20 rounded-full bg-[#7D8E95]/30 flex items-center justify-center">
                                <div className="w-12 h-12 rounded-full bg-[#7D8E95]/50 flex items-center justify-center">
                                    <Heart className="w-6 h-6 text-[#7D8E95]" />
                                </div>
                            </div>
                        </div>
                        <div className="text-center mb-8">
                            <Heading level={1} className="text-[#1D6B6B] text-2xl md:text-3xl font-medium mb-4 leading-relaxed">
                                Relationship Check-in
                            </Heading>
                            <BodyText className="text-[#7D8E95] text-lg leading-relaxed mb-4">
                                A gentle assessment to help you understand your relationship patterns.
                            </BodyText>
                            <BodyText className="text-[#7D8E95] text-base leading-relaxed">
                                Answer a few questions, and we'll guide you to the right tools.
                            </BodyText>
                        </div>
                        <BottomBarSpacer height="extended" />
                    </div>
                )}
                
                {step === 'welcome' && (
                    <PersistentBottomBar>
                        <PrimaryButton 
                            onClick={() => setStep('mode')} 
                            className="w-full" 
                            size="action" 
                            data-testid="start-checkin-btn"
                        >
                            Start Check-in
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                        <div className="text-center mt-1.5">
                            <button 
                                onClick={() => navigate('/home?tab=relationships')}
                                className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors"
                            >
                                Back to home
                            </button>
                        </div>
                    </PersistentBottomBar>
                )}

                {/* MODE SELECTION */}
                {step === 'mode' && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="text-center mb-8 pt-4">
                            <SmallText className="text-[#7D8E95] font-medium mb-3">How would you like to check in?</SmallText>
                            <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed">
                                Are you doing this alone or with your partner?
                            </Heading>
                        </div>
                        <div className="space-y-4 flex-1">
                            <button
                                onClick={() => handleModeSelect('solo')}
                                className="w-full p-6 rounded-2xl bg-[#7D8E95] text-left 
                                           transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]
                                           border-2 border-transparent hover:border-[#7D8E95]/20"
                                data-testid="mode-solo"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-full bg-white/50 flex items-center justify-center">
                                        <User className="w-7 h-7 text-[#7D8E95]" />
                                    </div>
                                    <div>
                                        <div className="font-medium text-[#1D6B6B] text-lg mb-1">Just me</div>
                                        <div className="text-[#7D8E95] text-sm">Reflect on your own, at your own pace</div>
                                    </div>
                                </div>
                            </button>
                            <button
                                onClick={() => handleModeSelect('partner')}
                                className="w-full p-6 rounded-2xl bg-[#7D8E95] text-left 
                                           transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]
                                           border-2 border-transparent hover:border-[#7D8E95]/20"
                                data-testid="mode-partner"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-full bg-white/50 flex items-center justify-center">
                                        <Users className="w-7 h-7 text-[#7D8E95]" />
                                    </div>
                                    <div>
                                        <div className="font-medium text-[#1D6B6B] text-lg mb-1">With my partner</div>
                                        <div className="text-[#7D8E95] text-sm">Answer together and compare perspectives</div>
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                )}

                {/* QUESTIONS */}
                {step === 'questions' && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        {(() => {
                            const q = questions[currentQuestion];
                            const QuestionIcon = q.icon;
                            return (
                                <>
                                    <div className="text-center mb-8 pt-4">
                                        <div className="w-14 h-14 rounded-full bg-[#7D8E95]/30 flex items-center justify-center mx-auto mb-4">
                                            <QuestionIcon className="w-6 h-6 text-[#7D8E95]" />
                                        </div>
                                        <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed mb-2">
                                            {q.question}
                                        </Heading>
                                        <BodyText className="text-[#7D8E95]">{q.subtext}</BodyText>
                                    </div>
                                    <div className="space-y-3 flex-1">
                                        {q.options.map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => handleAnswer(option.value)}
                                                className={`w-full p-5 rounded-2xl ${option.color} text-left 
                                                           transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]
                                                           border-2 border-transparent hover:border-[#7D8E95]/20
                                                           flex items-center gap-4`}
                                                data-testid={`answer-${option.value}`}
                                            >
                                                <div className="w-6 h-6 rounded-full border-2 border-[#7D8E95]/30 flex items-center justify-center">
                                                    {answers[q.id] === option.value && (
                                                        <div className="w-3 h-3 rounded-full bg-[#7D8E95]" />
                                                    )}
                                                </div>
                                                <span className="font-medium text-[#1D6B6B] text-lg">{option.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="mt-6 text-center">
                                        <SmallText className="text-[#7D8E95]">
                                            Question {currentQuestion + 1} of {questions.length}
                                        </SmallText>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* RESULTS */}
                {step === 'results' && results && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="text-center mb-6 pt-4">
                            <div className="w-14 h-14 rounded-full bg-[#FFBB98]/30 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-6 h-6 text-[#1D6B6B]" />
                            </div>
                            <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium leading-relaxed">
                                Here's what we noticed
                            </Heading>
                        </div>

                        {/* Patterns */}
                        <div className="space-y-4 mb-8">
                            {results.patterns.map((pattern) => {
                                const PatternIcon = pattern.icon;
                                const severityColors = {
                                    high: 'bg-[#FFBB98]/50 border-white/40',
                                    medium: 'bg-[#FFBB98]/50 border-white/40',
                                    low: 'bg-[#FFBB98]/50 border-white/40'
                                };
                                return (
                                    <div className="glass-card rounded-2xl p-5"
                                        key={pattern.id}
                                        variant="elevated"
                                        padding="md"
                                        className={`border ${severityColors[pattern.severity]}`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center flex-shrink-0">
                                                <PatternIcon className="w-5 h-5 text-[#7D8E95]" />
                                            </div>
                                            <div>
                                                <BodyText className="font-semibold text-[#1D6B6B] mb-1">{pattern.title}</BodyText>
                                                <SmallText className="text-[#7D8E95] leading-relaxed">{pattern.description}</SmallText>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Recommendations */}
                        <div className="mb-8">
                            <SmallText className="text-[#7D8E95] font-medium mb-4 block">
                                Tools that might help
                            </SmallText>
                            <div className="space-y-3">
                                {results.recommendations.map((rec, index) => {
                                    const RecIcon = rec.icon;
                                    return (
                                        <button
                                            key={rec.id}
                                            onClick={() => handleToolClick(rec)}
                                            className="w-full p-4 rounded-2xl bg-white border border-white/40 text-left
                                                     hover:border-[#7D8E95] hover:shadow-sm transition-all flex items-center gap-4
                                                     animate-fade-in"
                                            style={{ animationDelay: `${index * 100}ms` }}
                                            data-testid={`recommendation-${rec.id}`}
                                        >
                                            <div className={`w-11 h-11 rounded-full ${variantColors[rec.variant]} flex items-center justify-center`}>
                                                <RecIcon className={`w-5 h-5 ${variantIconColors[rec.variant]}`} />
                                            </div>
                                            <div className="flex-1">
                                                <BodyText className="font-medium text-[#1D6B6B]">{rec.title}</BodyText>
                                                <SmallText className="text-[#7D8E95]">{rec.description}</SmallText>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-[#7D8E95]" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="text-center mt-auto pb-8">
                            <button 
                                onClick={handleStartOver}
                                className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors"
                                data-testid="start-over-btn"
                            >
                                Start over
                            </button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default RelationshipCheckIn;
